import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { Tooltip } from 'antd'
import { isEmpty, isNil, map, some } from 'lodash'
import PropTypes from 'prop-types'

import { areSameValue } from '../../../core/utils'

import { TreeSelect } from '../../../common/components/sections'
import { StyledEditabledSelectContainer } from '../../../common/components/styled'
import { HOTKEYS_ENABLED_TAGS } from '../../../common/constants'
import {
  filterDuplicatesFromTreeData,
  filterTree,
  getTreeIdsFromLevelToLevel,
} from '../../../common/helpers'
import { treeOptionPropType } from '../../../common/prop-types'
import { findNodeByValue } from '../../../common/utils'

import { DISH_FIELDS_CONFIG, DISHES_AVAILABLE_HOTKEYS } from '../../constants'
import { getSuggestedByAiTreeData } from '../../helpers'
import {
  useDishEditableCellHotkey,
  useDishSuggestCurationAndUpdate,
  useUpdateDishMutation,
} from '../../hooks'
import { dishPropType } from '../../prop-types'
import { transformDishToUpdateArgs } from '../../transformers'

const DishEditableTreeSelectCell = ({
  fieldName,
  value,
  defaultComponent,
  isDisabled,
  treeData,
  dish,
  placeholder,
}) => {
  const [isEditing, setIsEditing] = useState(false)
  const [isDropdownVisible, setIsDropdownVisible] = useState(false)
  const selectRef = useRef(null)

  const [update, { isLoading }] = useUpdateDishMutation(dish?.id)
  const [suggestAndUpdate] = useDishSuggestCurationAndUpdate(dish.id)

  const { isSelectInHotkeyEditMode, setHotkeyEditModeEnabled } =
    useDishEditableCellHotkey(dish.id, fieldName)

  const { stickyTreeNodes = [], treeDefaultExpandedKeys = [] } = useMemo(() => {
    const suggestedByAiTreeData = getSuggestedByAiTreeData(
      treeData,
      dish?.aiSuggestion?.[fieldName]?.suggestion || [],
    )

    return {
      stickyTreeNodes: filterDuplicatesFromTreeData(suggestedByAiTreeData),
      treeDefaultExpandedKeys: getTreeIdsFromLevelToLevel(treeData, 1, 1),
    }
  }, [treeData, dish?.aiSuggestion, fieldName])

  const treeDataWithStickyNodes = useMemo(
    () =>
      isEmpty(stickyTreeNodes)
        ? treeData
        : [
            ...map(stickyTreeNodes, node => ({
              ...node,
              isSticky: true,
            })),
            ...filterTree(
              treeData,
              ({ value: nodeValue }) =>
                !some(stickyTreeNodes, ({ value: stickyNodeValue }) =>
                  areSameValue(nodeValue, stickyNodeValue),
                ),
              () => true,
            ),
          ],
    [stickyTreeNodes, treeData],
  )

  const selectedNode = useMemo(
    () => findNodeByValue(treeData, value),
    [treeData, value],
  )

  const handleChange = useCallback(
    newValue => {
      if (
        fieldName === DISH_FIELDS_CONFIG.DISH_TYPE.value &&
        !isNil(newValue)
      ) {
        const selectedNewValue = findNodeByValue(treeData, newValue)

        if (!selectedNewValue?.isIgnored) {
          return suggestAndUpdate(
            { shouldSuggestOnlyCourseTypeAndIngredients: true },
            { dishTypeId: newValue },
          )
        }
      }

      return update(transformDishToUpdateArgs({ [fieldName]: newValue }))
    },
    [fieldName, update, treeData, suggestAndUpdate],
  )

  useEffect(() => {
    if (isSelectInHotkeyEditMode) {
      setIsEditing(true)
    }
  }, [isSelectInHotkeyEditMode])

  const handleClick = useCallback(
    e => {
      e.stopPropagation()
      if (!isDisabled) {
        setIsEditing(true)
      }
    },
    [isDisabled],
  )

  useEffect(() => {
    if (!isNil(selectRef.current)) {
      selectRef.current.focus()
    }
  }, [isEditing])

  const handleBlur = useCallback(() => {
    setIsEditing(false)
    setHotkeyEditModeEnabled(false)
  }, [setIsEditing, setHotkeyEditModeEnabled])

  useHotkeys(
    DISHES_AVAILABLE_HOTKEYS.ESC.hotkey,
    handleBlur,
    {
      enabled: isEditing,
      enableOnTags: HOTKEYS_ENABLED_TAGS,
    },
    [isEditing, handleBlur],
  )

  useEffect(() => {
    handleBlur()
  }, [handleBlur, isLoading])

  return (
    <StyledEditabledSelectContainer
      onClick={handleClick}
      role="presentation"
      isDisabled={isDisabled}
    >
      {isEditing && !isDisabled ? (
        <Tooltip title={isDropdownVisible ? null : selectedNode?.namePath}>
          <TreeSelect
            ref={selectRef}
            showSearch
            placeholder={placeholder}
            disabled={isDisabled}
            value={value}
            treeData={treeDataWithStickyNodes}
            treeDefaultExpandedKeys={treeDefaultExpandedKeys}
            onDropdownVisibleChange={setIsDropdownVisible}
            dropdownStyle={{ maxHeight: 400, minWidth: 250 }}
            dropdownClassName="dark-dropdown-highlights"
            onClick={handleClick}
            onChange={handleChange}
            onBlur={handleBlur}
            maxTagCount={null}
          />
        </Tooltip>
      ) : (
        defaultComponent
      )}
    </StyledEditabledSelectContainer>
  )
}

DishEditableTreeSelectCell.propTypes = {
  fieldName: PropTypes.string.isRequired,
  defaultComponent: PropTypes.node.isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  isDisabled: PropTypes.bool,
  dish: dishPropType.isRequired,
  placeholder: PropTypes.string,
  treeData: PropTypes.arrayOf(treeOptionPropType),
}

DishEditableTreeSelectCell.defaultProps = {
  isDisabled: false,
  value: null,
  placeholder: undefined,
  treeData: [],
}

export default DishEditableTreeSelectCell
